(() => {
  // Sets up a global object to hold custom functions and classes.
  window.mx = window.mx || {};

  /**
   * Manages event listeners for easy addition and removal.
   */
  window.mx.EventManager = class {
    constructor() {
      this._listeners = new Map();
    }

    /**
     * Adds an event listener and manages it for easy removal.
     * @param {EventTarget} element - The element to attach the event listener to.
     * @param {String} type - The type of event to listen for.
     * @param {Function} handler - The function to be called when the event occurs.
     * @param {Object} options - Options for the event listener.
     */
    addListener(element, type, handler, options) {
      if (!(element instanceof EventTarget)) {
        throw new Error("Invalid element provided to addListener");
      }

      element.addEventListener(type, handler, options);

      if (!this._listeners.has(element)) {
        this._listeners.set(element, new Set());
      }
      this._listeners.get(element).add({ type, handler });
    }

    /**
     * Removes a listener for the event.
     */
    removeListener(element, type, handler) {
      element.removeEventListener(type, handler);
    }

    /**
     * Removes all managed event listeners.
     */
    removeAllListeners() {
      for (const [element, listeners] of this._listeners) {
        listeners.forEach(({ type, handler }) => {
          this.removeListener(element, type, handler);
        });
      }
      this._listeners.clear();
    }
  };

  window.mx.MxComponent = class extends HTMLElement {
    constructor() {
      super();
      this.eventManager = new window.mx.EventManager();
    }

    connectedCallback() {
      // Use this.eventManager.addListener to add easy cleanup event listeners.
    }

    disconnectedCallback() {
      this.eventManager.removeAllListeners();
    }
  };
  if (!window.customElements.get("mx-component")) {
    window.customElements.define("mx-component", window.mx.MxComponent);
  }

  const GiftWrapSelector = class extends window.mx.MxComponent {
    constructor() {
      super();
    }

    connectedCallback() {
      this.eventManager.addListener(this.input, "change", () =>
        this.onInputChange(),
      );
    }

    get section() {
      return document.querySelector(".shopify-section--cart-drawer");
    }
    get cartDrawer() {
      return document.querySelector("#cart-drawer");
    }
    get input() {
      return this.querySelector("input");
    }
    get label() {
      return this.querySelector("label");
    }
    get variantId() {
      return this.getAttribute("data-variant-id");
    }

    async onInputChange() {
      try {
        this.setAttribute("inert", "");
        const sectionId = this.section.id.split("shopify-section-").pop();

        if (this.input.checked) {
          const addToCartJson = await fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items: [{ id: this.variantId, quantity: 1 }],
              sections: [sectionId],
            }),
          }).then((response) => response.json());

          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: {
                baseEvent: "variant:add",
                cart: addToCartJson,
                sections: [sectionId],
              },
            }),
          );
        } else {
          const cartChangeJson = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              id: this.variantId,
              quantity: 0,
              sections: [sectionId],
            }),
          }).then((response) => response.json());

          document.documentElement.dispatchEvent(
            new CustomEvent("cart:change", {
              bubbles: true,
              detail: {
                baseEvent: "line-item:change",
                cart: cartChangeJson,
                sections: [sectionId],
              },
            }),
          );
        }
      } catch (error) {
        console.error("Error selecting gift wrapping:", error);
      } finally {
        this.input.checked
          ? this.removeAttribute("checked")
          : this.setAttribute("checked", "");
        this.removeAttribute("inert");
      }
    }
  };
  if (!customElements.get("gift-wrap-selector")) {
    customElements.define("gift-wrap-selector", GiftWrapSelector);
  }
})();
